import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import { Box, Typography } from '@mui/material';
import {
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useClickAway, usePreviousDistinct } from 'react-use';

import { Markdown } from '@/components/Markdown/Markdown';
import {
  HORIZONTAL_RULE_NODES,
  horizontalRuleTransformer,
} from '@/components/TextEditor/horizontalRule';
import { TextEditor } from '@/components/TextEditor/TextEditor';
import {
  EditorInner,
  TextEditorWrapper as TextEditorWrapperExternal,
} from '@/components/TextEditor/TextEditor.utils';
import { useOnBeforeUnloadPrompt } from '@/hooks/useOnBeforeUnloadPrompt';
import { canonicalizeSummaryForDirtyCheck } from '@/modules/documents/documents.utils';
import { useRegisterSlideNavDirtyGetter } from '@/modules/presentation/clientPresentation/context/SlideNavGuard.context';
import { AcceptRejectToolbarButtons } from '@/modules/slideEditor/AcceptRejectToolbarButtons';
import { TenantSlideStyle } from '@/modules/slideEditor/coverSlide/CoverSlide.types';
import { extractMermaidCode } from '@/modules/slideEditor/customSlide/mermaidDetection';
import { bodyTextAlignmentToCss } from '@/modules/slideEditor/shared/bodyTextAlignmentConfig';
import { COLORS } from '@/styles/tokens/colors';

import { useSlideEditorPromptContext } from '../textEditors/SlideEditor.promptContext';
import { useSlideEditorInlineToolbarPlugin } from '../textEditors/SlideEditor.toolbarPlugins';
import { VariableTypeaheadPlugin } from '../textEditors/SlideEditor.variableTypeaheadPlugin';
import { useVariableHighlight } from './hooks/useVariableHighlight';
import {
  SLIDE_EDITOR_HOVER,
  VARIABLE_HIGHLIGHT_HOVER_SX,
  VARIABLE_HIGHLIGHT_STYLES,
  VARIABLE_HIGHLIGHT_SX,
} from './SlideEditor.constants';
import { isClickInsideEditorPortal } from './utils/isClickInsideEditorPortal';

// Stable reference so the editor's transformer list isn't rebuilt each render.
const HORIZONTAL_RULE_TRANSFORMERS = [horizontalRuleTransformer];

export interface SlideEditorRichTextEditor {
  content: string;
  /** When provided, the editor uses this as initialValue instead of content.
   *  Useful when the preview display differs from what the user should edit. */
  editableContent?: string;
  /** Fires once when the user finishes editing — on click-away with no
   *  `onClickAway` provided, or on unmount while still editing. The
   *  argument is the latest content; only fires if it differs from the
   *  value the editor started this edit session with (whitespace-
   *  canonicalized comparison).
   *
   *  When omitted, the editor renders read-only unless `onRawUpdate` is
   *  provided (e.g., consumers that drive saving from a Save button via
   *  a draft ref). */
  onSubmit?: (content: string) => void;
  onClick?: () => void;
  /** When true, `{Variable name}` tokens are highlighted with a colored
   *  background. Intended for template editing; live presentations should
   *  leave this off. */
  highlightVariables?: boolean;
  /** Additional toolbar buttons rendered before the default block/format/table
   *  buttons. Typically used to add prompt and variable buttons on cover and
   *  custom slides. */
  additionalToolbarButtons?: ReactNode;
  /** Additional toolbar buttons rendered after the default block/format/table
   *  buttons. Used for slide-specific controls like Save/Cancel. */
  endToolbarButtons?: ReactNode;
  /** Custom empty-state component shown when `content` is empty and the editor
   *  is not active. When omitted, a default "Click to add custom text" message
   *  is shown. */
  emptyState?: ReactNode;
  /** When true, disables the blue outline hover effect on the non-editing
   *  wrapper. Useful when the parent already provides its own border/outline. */
  disableHover?: boolean;
  /** Called when the user clicks outside the editor while editing. When
   *  provided, the editor does NOT auto-close — the callback is responsible
   *  for deciding whether to close (e.g., after showing a confirmation). */
  onClickAway?: () => void;
  /** Called on every editor update with the raw content string, even when
   *  the content matches the original (unlike onSubmit which is guarded by
   *  a canonicalization diff). Useful for keeping a draft ref in sync. */
  onRawUpdate?: (content: string) => void;
  /** Optional tenant-level style overrides for the rich text content. */
  tenantStyle?: TenantSlideStyle;
}

export function SlideEditorRichTextEditor({
  content,
  editableContent,
  onSubmit: onSubmitExternal,
  onClick: onClickExternal,
  highlightVariables,
  additionalToolbarButtons,
  endToolbarButtons,
  emptyState,
  disableHover,
  onClickAway,
  onRawUpdate,
  tenantStyle,
}: SlideEditorRichTextEditor) {
  const {
    nodes: variableNodes,
    displayPlugins: variableDisplayPlugins,
    editPlugins: variableEditPlugins,
    resolverVersion,
  } = useVariableHighlight();
  // Register the horizontal rule node alongside the variable-highlight nodes
  // so `- - -` / `---` markdown renders (and round-trips) as a branded rule.
  const editorNodes = useMemo(
    () => [...variableNodes, ...HORIZONTAL_RULE_NODES],
    [variableNodes]
  );
  const promptCtx = useSlideEditorPromptContext();
  const pendingContent = promptCtx?.pendingContent ?? null;
  const isPending = pendingContent != null;
  const [isEditing, setIsEditing] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  // Transitioning into pending mode (generation just completed): force the
  // editor into edit mode so the user can refine the regenerated content
  // inline before choosing to accept or reject. Transitioning back out
  // (Accept or Reject): blur the editor so the floating toolbar dismisses
  // and the slide returns to its non-editing state.
  const prevIsPending = usePreviousDistinct(isPending);
  useEffect(() => {
    if (isPending) {
      setIsEditing(true);
    } else if (prevIsPending) {
      setIsEditing(false);
    }
  }, [isPending, prevIsPending]);

  // While a regenerated draft is pending, prompt on slide-to-slide navigation
  // with the "Unsaved changes" modal; choosing "Discard changes" drops the
  // draft via `rejectPendingContent` so the user lands on the next slide clean.
  // Safe (no-op) outside the guard's provider tree.
  useRegisterSlideNavDirtyGetter(isPending, promptCtx?.rejectPendingContent);
  // Block browser-level navigation (refresh, close, history) for as long as the
  // editor is open. `isEditing` is forced true while a draft is pending, so
  // this also covers the regenerated-draft case. The editor commits on blur, so
  // keying off edit mode (not a content diff) covers typing, paste, and toolbar
  // edits. No-op outside an UnloadPromptProvider.
  useOnBeforeUnloadPrompt(isEditing);

  // Live latest content; updated per-keystroke via the editor's onUpdate.
  // Read once on commit so onSubmit fires a single update rather than per
  // keystroke (the toolbar re-renders on form changes, which would jump
  // the user's caret if onSubmit fired during typing).
  const latestContentRef = useRef<string>(editableContent ?? content);

  // Mirror state/props into refs for the unmount-cleanup effect (which
  // has [] deps so it can't read them directly without going stale).
  // Each gets its own narrow-deps effect so we don't churn on every
  // parent re-render.
  const isEditingRef = useRef(isEditing);
  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);
  const onSubmitRef = useRef(onSubmitExternal);
  useEffect(() => {
    onSubmitRef.current = onSubmitExternal;
  }, [onSubmitExternal]);
  const onClickAwayRef = useRef(onClickAway);
  useEffect(() => {
    onClickAwayRef.current = onClickAway;
  }, [onClickAway]);
  // Latest content prop, mirrored for the click-away / unmount diff
  // check. We compare against the live prop (not a session-start
  // snapshot) so external content updates during the session — e.g. an
  // AI-generated replacement — don't trigger a spurious commit when the
  // user clicks away without further changes.
  const contentPropRef = useRef<string>(editableContent ?? content);
  useEffect(() => {
    contentPropRef.current = editableContent ?? content;
  }, [editableContent, content]);

  function commitIfDirty() {
    const onSubmit = onSubmitRef.current;
    if (!onSubmit) return;
    const next = latestContentRef.current;
    const original = contentPropRef.current;
    // Markdown roundtripping can rewrite whitespace without semantic
    // change; canonicalize before comparing so we don't fire onSubmit
    // for no-op edits.
    if (
      canonicalizeSummaryForDirtyCheck(next) !==
      canonicalizeSummaryForDirtyCheck(original)
    ) {
      onSubmit(next);
    }
  }

  useClickAway(editorRef, (event) => {
    if (isClickInsideEditorPortal(event.target)) return;
    if (!isEditing) return;

    // While pending, the accept/reject buttons are the only way to leave
    // the staging state. Don't auto-commit or close on outside clicks.
    if (isPending) return;

    if (onClickAway) {
      // Consumer manages closing/saving themselves (e.g., to show an
      // unsaved-changes confirmation). Don't auto-commit.
      onClickAway();
      return;
    }

    commitIfDirty();
    setIsEditing(false);
  });

  // Mirror the pending flag so the unmount effect (which runs with stale
  // [] deps) can read the latest value without a re-bind cycle.
  const isPendingRef = useRef(isPending);
  useEffect(() => {
    isPendingRef.current = isPending;
  }, [isPending]);

  // Flush any unsaved edit on unmount — useClickAway only fires on
  // outside mousedown, so unmounts driven by other state (e.g., the user
  // selecting a different slide while editing) would otherwise drop
  // typed content. Skip when the consumer provided `onClickAway`; in
  // that case they own the save/discard decision and an automatic
  // commit here would bypass their flow.
  useEffect(() => {
    return () => {
      if (!isEditingRef.current) return;
      if (onClickAwayRef.current) return;
      // While a regenerated draft is pending, the editor content is the
      // staged value — not something we should persist on unmount. The
      // accept/reject controls are the only persistence path here.
      if (isPendingRef.current) return;
      const onSubmit = onSubmitRef.current;
      if (!onSubmit) return;
      const next = latestContentRef.current;
      const original = contentPropRef.current;
      if (
        canonicalizeSummaryForDirtyCheck(next) !==
        canonicalizeSummaryForDirtyCheck(original)
      ) {
        onSubmit(next);
      }
    };
  }, []);

  // Memoized handlers so the AcceptRejectToolbarButtons element identity
  // is stable for the toolbar plugin's ref-based prop snapshotting.
  const handleAcceptPending = useCallback(() => {
    promptCtx?.acceptPendingContent();
  }, [promptCtx]);
  const handleRejectPending = useCallback(() => {
    promptCtx?.rejectPendingContent();
  }, [promptCtx]);

  // While a regenerated draft is pending, surface Accept / Reject at the
  // end of the floating toolbar. Consumer-provided endToolbarButtons are
  // preserved so any slide-specific actions remain available.
  const effectiveEndToolbarButtons = isPending ? (
    <>
      {endToolbarButtons}
      <AcceptRejectToolbarButtons
        onAccept={handleAcceptPending}
        onReject={handleRejectPending}
        acceptDataTestId="SlideEditorRichTextEditor-acceptButton"
        rejectDataTestId="SlideEditorRichTextEditor-rejectButton"
      />
    </>
  ) : (
    endToolbarButtons
  );

  const ToolbarPlugin = useSlideEditorInlineToolbarPlugin({
    additionalButtons: additionalToolbarButtons,
    endButtons: effectiveEndToolbarButtons,
  });

  const isMermaid = extractMermaidCode(content) !== null;
  const isStreaming = promptCtx?.generationStatus === 'generating';

  // Tenant-branded body color is applied at the Lexical theme layer
  // (via `bodyTextColor` on `<Markdown>`/`<TextEditor>`), not as a
  // descendant `sx` rule here. A parent `& *: inherit` rule shares
  // specificity (0,1,0) with the Lexical class colors on `<p>`/`<h*>`,
  // so the cascade resolved by source order — non-deterministic across
  // mount paths and producing sporadic regressions in read-only/print.
  const bodyTextColor = tenantStyle?.bodyTextColor;
  // Tenant-branded body alignment is applied at the Lexical theme layer
  // (via `bodyTextAlignment` on `<Markdown>`/`<TextEditor>`), mirroring
  // how `bodyTextColor` is threaded. Absent → left-aligned default.
  const bodyTextAlignment = bodyTextAlignmentToCss(
    tenantStyle?.bodyTextAlignment
  );

  // Render read-only when the consumer hasn't wired up either save path.
  // `onRawUpdate` alone is enough — some consumers (e.g., notes editors
  // with an explicit Save button) drive persistence from a draft ref and
  // have no use for the auto-commit `onSubmit`.
  if (!onSubmitExternal && !onRawUpdate) {
    const inner =
      highlightVariables && editableContent
        ? PromptMarkdownInner
        : MarkdownInner;

    return (
      <Box
        sx={[
          highlightVariables ? VARIABLE_HIGHLIGHT_SX : {},
          ...(bodyTextAlignment ? [{ textAlign: bodyTextAlignment }] : []),
        ]}
      >
        <Markdown
          key={resolverVersion}
          markdown={content}
          EditorInner={inner}
          additionalNodes={editorNodes}
          additionalTransformers={HORIZONTAL_RULE_TRANSFORMERS}
          additionalPlugins={variableDisplayPlugins}
          bodyTextColor={bodyTextColor}
          bodyTextAlignment={bodyTextAlignment}
        />
      </Box>
    );
  }

  // LEGACY (Mermaid): Mermaid diagrams have their own hover/interaction UI via
  // Estate360MermaidRenderer, so skip the SLIDE_EDITOR_HOVER wrapper (which
  // constrains width via fit-content and adds a redundant blue outline on hover).
  // This bypass preserves legacy Mermaid interaction UI for existing custom pages.
  // Tracked by T2-10286; revisit ~2027-06-01 to assess removal.
  if (isMermaid) {
    return <Markdown markdown={content} EditorInner={MarkdownInner} />;
  }

  // During streaming, show a read-only Markdown preview of the accumulating
  // AI-generated text (visible behind the full-screen loading overlay). The
  // editor is not mounted during streaming to avoid persisting partial content
  // to the form.
  if (isStreaming) {
    const displayText = promptCtx.streamedContent || '';
    return (
      <Box aria-busy="true" aria-label="Generating content">
        <Markdown
          markdown={displayText}
          EditorInner={MarkdownInner}
          additionalNodes={HORIZONTAL_RULE_NODES}
          additionalTransformers={HORIZONTAL_RULE_TRANSFORMERS}
          bodyTextColor={bodyTextColor}
          bodyTextAlignment={bodyTextAlignment}
        />
      </Box>
    );
  }

  if (isEditing) {
    return (
      <Box
        ref={editorRef}
        sx={[
          {
            position: 'relative', // necessary to not reserve space for the toolbar
            width: '100%',
            height: '100%',
            ...(bodyTextAlignment && { textAlign: bodyTextAlignment }),
          },
          VARIABLE_HIGHLIGHT_SX,
        ]}
      >
        <TextEditor
          key={promptCtx?.promptApplyVersion ?? 0}
          initialValue={pendingContent ?? editableContent ?? content}
          initialValueKind="markdown"
          onImmediateUpdate={(str) => {
            // Track latest content for the deferred commit on click-away
            // / unmount. Per-keystroke onSubmit firing was removed (along
            // with the upstream debouncing) so a single update fires per
            // edit session.
            latestContentRef.current = str;
            if (isPending) {
              // While a regenerated draft is pending, mirror every edit
              // into the prompt context so Accept persists the user's
              // refinements rather than the original generated text.
              // Don't fire onRawUpdate: consumers use it as a draft side
              // channel that may persist outside the accept/reject gate
              // (e.g. EntityDiagramAndNotesSlide), and the staged content
              // must not leak before the user accepts.
              promptCtx?.updatePendingContentDraft(str);
              return;
            }
            onRawUpdate?.(str);
          }}
          ToolbarPlugin={ToolbarPlugin}
          TextEditorWrapper={TextEditorWrapper}
          EditorInner={TextEditorInner}
          additionalNodes={editorNodes}
          additionalTransformers={HORIZONTAL_RULE_TRANSFORMERS}
          additionalPlugins={
            <>
              {variableEditPlugins}
              <VariableTypeaheadPlugin />
              {/* Lets a rule be clicked-to-select and deleted; insertion is
                  via the `---` markdown shortcut (no toolbar button). */}
              <HorizontalRulePlugin />
            </>
          }
          bodyTextColor={bodyTextColor}
          bodyTextAlignment={bodyTextAlignment}
        />
      </Box>
    );
  }

  return (
    <Box
      onClick={() => {
        setIsEditing(true);
        onClickExternal?.();
      }}
      sx={[
        ...(disableHover
          ? [{ width: '100%', height: '100%' }]
          : [SLIDE_EDITOR_HOVER]),
        highlightVariables
          ? VARIABLE_HIGHLIGHT_SX
          : VARIABLE_HIGHLIGHT_HOVER_SX,
        { cursor: 'text' },
        // SLIDE_EDITOR_HOVER shrink-wraps the box to `fit-content`, which
        // leaves `text-align` no room to shift the paragraphs. When a
        // tenant alignment is set, stretch to full width and set
        // `text-align` so both the rendered markdown and the empty-state
        // placeholder shift (last entry wins over the fit-content above).
        ...(bodyTextAlignment
          ? [{ width: '100%', textAlign: bodyTextAlignment }]
          : []),
      ]}
    >
      {content ? (
        <Markdown
          key={resolverVersion}
          markdown={content}
          EditorInner={
            highlightVariables && editableContent
              ? PromptMarkdownInner
              : MarkdownInner
          }
          additionalNodes={editorNodes}
          additionalTransformers={HORIZONTAL_RULE_TRANSFORMERS}
          additionalPlugins={variableDisplayPlugins}
          bodyTextColor={bodyTextColor}
          bodyTextAlignment={bodyTextAlignment}
        />
      ) : (
        (emptyState ?? (
          <Typography
            variant="body-s"
            sx={{
              color: COLORS.TEXT.PLACEHOLDER,
            }}
          >
            Click to add custom text
          </Typography>
        ))
      )}
    </Box>
  );
}

function MarkdownInner({ children }: PropsWithChildren) {
  return (
    <EditorInner borderRadius={0} sx={{ background: 'transparent' }}>
      {children}
    </EditorInner>
  );
}

function PromptMarkdownInner({ children }: PropsWithChildren) {
  return (
    <EditorInner
      borderRadius="4px"
      sx={{
        // Override EditorInner's white background so the prompt block
        // gets the same visual treatment as variable chips. Use `background`
        // (not `backgroundColor`) to override EditorInner's own `background` shorthand.
        background: VARIABLE_HIGHLIGHT_STYLES.backgroundColor,
        boxShadow: VARIABLE_HIGHLIGHT_STYLES.boxShadow,
        // Use a descendant selector to override Lexical's rendered text color,
        // matching the approach used by VARIABLE_HIGHLIGHT_SX for variable chips.
        '& *': { color: VARIABLE_HIGHLIGHT_STYLES.color },
      }}
    >
      {children}
    </EditorInner>
  );
}

function TextEditorWrapper({ children }: PropsWithChildren) {
  return (
    <TextEditorWrapperExternal height="100%" hideBorder>
      {children}
    </TextEditorWrapperExternal>
  );
}

function TextEditorInner({ children }: PropsWithChildren) {
  return (
    <EditorInner
      borderRadius={0}
      sx={{
        background: 'transparent',
        overflow: 'auto',
        flexShrink: 1,
        minHeight: 0,
      }}
    >
      {children}
    </EditorInner>
  );
}
